/**
 * The participation moment, resolved: when the fan committed, the probability stored with that
 * lock, and what changed after it (verified lock-moment score → final). Without a call: a reason
 * to make one next time — never a "you called it".
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { directorClock, type DirectorResponse } from '@clutch/core';
import { teamAccent } from '../ui/color';
import { reducedMotion } from '../stage/Stage';
import { localTeamName, sinceCall, situationText } from './moment-scene';
import { pick, normalizeLanguage } from './i18n';

type D = Pick<DirectorResponse, 'call' | 'asOf' | 'game'> & Partial<Pick<DirectorResponse, 'moments'>>;

export function receiptLine(d: D, l: string, who?: string): string | undefined {
  const c = d.call;
  if (!c) return undefined;
  const team = localTeamName(c.chosenTeam, l);
  const clock = directorClock(c.lockedAt.period, c.lockedAt.clockSec, normalizeLanguage(l));
  const pct = Math.round(c.pChoiceAtLock * 100);
  const subj = who ?? undefined;
  if (c.result === 'correct')
    return subj
      ? pick({ en: `${subj} called it at ${clock}, when the ${team} had a ${pct}% chance.`, hi: `${subj} ने ${clock} पर यह कॉल लगाई थी, जब ${team} की संभावना सिर्फ़ ${pct}% थी।`, zh: `${subj}在${clock}做出预测，当时${team}的模型胜率为${pct}%。` }, l)
      : pick({ en: `You called it at ${clock}, when the ${team} had a ${pct}% chance.`, hi: `आपने ${clock} पर यह कॉल लगाई थी, जब ${team} की संभावना सिर्फ़ ${pct}% थी।`, zh: `你在${clock}做出预测，当时${team}的模型胜率为${pct}%。` }, l);
  if (c.result === 'incorrect')
    return subj
      ? pick({ en: `${subj} backed the ${team} at ${clock}. The model had them at ${pct}%.`, hi: `${subj} ने ${clock} पर ${team} को चुना। मॉडल ने उन्हें ${pct}% दिया था।`, zh: `${subj}在${clock}看好${team}，模型给出的胜率是${pct}%。` }, l)
      : pick({ en: `You backed the ${team} at ${clock}. The model had them at ${pct}%.`, hi: `आपने ${clock} पर ${team} को चुना। मॉडल ने उन्हें ${pct}% दिया था।`, zh: `你在${clock}看好${team}，模型给出的胜率是${pct}%。` }, l);
  return pick({ en: `Locked at ${clock} · ${team} ${pct}%`, hi: `${clock} पर लॉक · ${team} ${pct}%`, zh: `${clock}锁定 · ${team} ${pct}%` }, l);
}

export function CallReceipt({ d, l, who }: { d: D; l: string; who?: string }) {
  const c = d.call!;
  const since = sinceCall({ call: c, moments: d.moments ?? [], asOf: d.asOf, game: d.game });
  const pct = Math.round(c.pChoiceAtLock * 100);
  const accent = teamAccent(c.chosenTeam);
  const [shown, setShown] = useState(reducedMotion() ? pct : 0);
  useEffect(() => {
    if (reducedMotion()) return setShown(pct);
    const t = setTimeout(() => setShown(pct), 350);
    return () => clearTimeout(t);
  }, [pct]);
  const lockAt = d.moments?.find((m) => m.kind === 'call');
  const away = d.game.away.tricode;
  const home = d.game.home.tricode;
  return (
    <div className={`callrcpt callrcpt--${c.result}`} style={{ '--t': accent } as CSSProperties} data-testid="call-stamp">
      <div className="callrcpt__top">
        <span className="callrcpt__pct"><b>{shown}</b><i>%</i></span>
        <span className="callrcpt__line" data-testid="call-receipt">{receiptLine(d, l, who)}</span>
      </div>
      <div className="callrcpt__bar" aria-hidden="true"><i style={{ width: `${shown}%` }} /></div>
      {since && lockAt && (
        <dl className="callrcpt__changed" data-testid="what-changed">
          <div>
            <dt>{pick({ en: 'At the call', hi: 'कॉल के समय', zh: '预测时' }, l)}</dt>
            <dd>{situationText(d, since.lockMargin, lockAt.awayScore, lockAt.homeScore, l)}</dd>
          </div>
          <span aria-hidden="true">→</span>
          <div>
            <dt>{d.asOf.status === 'final' ? pick({ en: 'Final', hi: 'अंतिम', zh: '终场' }, l) : pick({ en: 'Now', hi: 'अभी', zh: '目前' }, l)}</dt>
            <dd>{situationText(d, since.finalMargin, d.asOf.awayScore, d.asOf.homeScore, l)}</dd>
          </div>
          <p>
            {pick({ en: 'After the call', hi: 'कॉल के बाद', zh: '预测之后' }, l)}: <b>{away} {since.away}</b> · <b>{home} {since.home}</b>
          </p>
        </dl>
      )}
      <small className="callrcpt__model">{c.modelVersion} · {pick({ en: 'stored at lock', hi: 'लॉक पर सहेजा गया', zh: '锁定时保存' }, l)}</small>
    </div>
  );
}

/** No call this time: what Clutch would have saved, and how to get it next time. */
export function NextCallHook({ l, pending }: { l: string; pending?: boolean }) {
  if (pending)
    return (
      <div className="hook" data-testid="next-call-hook">
        <b>{pick({ en: 'Who wins from here?', hi: 'यहाँ से कौन जीतेगा?', zh: '接下来谁会赢？' }, l)}</b>
        <span>{pick({ en: 'The finish is still to play. Watch it and lock your call: Clutch saves the odds at the second you commit.', hi: 'फ़िनिश अभी बाक़ी है। देखिए और अपनी कॉल लॉक कीजिए: Clutch आपकी कॉल के पल की संभावना सहेजता है।', zh: '结局尚未揭晓。观看比赛并锁定你的预测：Clutch会保存你做出预测那一刻的胜率。' }, l)}</span>
      </div>
    );
  return (
    <div className="hook" data-testid="next-call-hook">
      <b>{pick({ en: 'Next time, make your call.', hi: 'अगली बार, अपनी कॉल लगाइए।', zh: '下次，做出你的预测。' }, l)}</b>
      <span>{pick({ en: 'In a close finish Clutch asks one question — who wins? — and saves the odds at the second you commit. Beat them and it becomes your story.', hi: 'कड़े मुक़ाबले में Clutch एक सवाल पूछता है — कौन जीतेगा? — और आपकी कॉल के पल की संभावना सहेज लेता है। उसे मात दीजिए, वह आपकी कहानी बनेगी।', zh: '比赛胶着时，Clutch只问一个问题——谁会赢？并保存你做出预测那一刻的胜率。赢过它，这就是你的故事。' }, l)}</span>
    </div>
  );
}
